import React from "react";

import { Impulse, useScoped } from "~/tools/impulse";

import {
	STATIC_TIME_RANGES,
	type SpecificRange,
	type TimeRange,
} from "../../../../../app/state/view/timeline/TimeRange";

import { TimeRangeSelectorContentCustom } from "./TimeRangeSelectorContentCustom";
import { TimeRangeSelectorContentList } from "./TimeRangeSelectorContentList";

export interface TimeRangeSelectorContentProps {
	timeRange: Impulse<TimeRange>;
	recentTimeRanges: undefined | ReadonlyArray<SpecificRange>;
	closePopup: VoidFunction;
	onClear: VoidFunction;
}

export const TimeRangeSelectorContent = React.forwardRef<HTMLDivElement, TimeRangeSelectorContentProps>(
	({ timeRange: timeRangeFromProps, recentTimeRanges = [], closePopup, onClear }, ref) => {
		const timeRange = React.useMemo(
			() =>
				Impulse(timeRangeFromProps, (next) => {
					closePopup();
					timeRangeFromProps.setValue(next);
				}),
			[closePopup, timeRangeFromProps],
		);
		const timeRangeValue = useScoped(timeRange);
		const defaultValue = timeRangeValue.isSpecific() ? timeRangeValue.toInterval() : undefined;
		const handleClear = () => {
			closePopup();
			onClear();
		};

		return (
			<div className="flex w-141.25 divide-x divide-solid divide-gray-400 bg-white" ref={ref}>
				<TimeRangeSelectorContentList
					className="shrink-0 p-6"
					data-testid="time-range-relative-intervals"
					timeRange={timeRange}
					timeRanges={STATIC_TIME_RANGES}
					title="Relative intervals"
				/>

				<div className="space-y-6 border-0 p-6">
					<TimeRangeSelectorContentCustom
						defaultValue={defaultValue}
						onChange={(next) => timeRange.setValue(next)}
						onClear={handleClear}
					/>

					{recentTimeRanges.length > 0 && (
						<TimeRangeSelectorContentList
							data-testid="time-range-recently-used"
							timeRange={timeRange}
							timeRanges={recentTimeRanges}
							title="Recently used"
						/>
					)}
				</div>
			</div>
		);
	},
);
